/**
 * Alpine.js Quick Search Filters Component
 * Handles the discover people filter panel with localStorage persistence
 */

import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';

function getLocalStorage() {
    try {
        if (typeof window === 'undefined') {
            return null;
        }

        const storage = window.localStorage;
        if (!storage
            || typeof storage.getItem !== 'function'
            || typeof storage.setItem !== 'function') {
            return null;
        }

        return storage;
    } catch (e) {
        return null;
    }
}

function getStoredValue(key) {
    try {
        return getLocalStorage()?.getItem(key) ?? null;
    } catch (e) {
        return null;
    }
}

function setStoredValue(key, value) {
    try {
        getLocalStorage()?.setItem(key, value);
    } catch (e) {
        // localStorage may be unavailable
    }
}

/**
 * Creates a quick search filter component
 * @param {Object} config - Initial configuration
 * @param {number} config.min - Current minimum age
 * @param {number} config.max - Current maximum age
 * @param {number} config.rangeMin - Minimum allowed age
 * @param {number} config.rangeMax - Maximum allowed age
 * @param {boolean} config.male - Male filter enabled
 * @param {boolean} config.female - Female filter enabled
 * @param {Array<{code: string, name: string}>} config.selectedCountries - Initial countries from server qsearch settings
 * @param {Array<{code: string, name: string}>} config.selectedLanguages - Initial languages from server qsearch settings
 */
export function quickSearchFilters(config = {}) {
    return {
        filtersOpen: false,
        qsChipOpen: null,
        qsPopoverStyle: '',
        qsCollapsed: !!getStoredValue('qs_collapsed'),
        min: config.min ?? 16,
        max: config.max ?? 65,
        rangeMin: config.rangeMin ?? 16,
        rangeMax: config.rangeMax ?? 65,
        male: config.male ?? false,
        female: config.female ?? false,
        selectedCountries: Array.isArray(config.selectedCountries) ? config.selectedCountries : [],
        selectedLanguages: Array.isArray(config.selectedLanguages) ? config.selectedLanguages : [],
        online: false,
        debounceTimer: null,
        storageKey: 'discover-filters',
        translations: config.translations ?? { searchCountries: 'Search countries', anywhere: 'Anywhere' },

        openChipPopover(chipName, event) {
            if (this.qsChipOpen === chipName) {
                this.qsChipOpen = null;
                return;
            }
            // Desktop: position popover just below the clicked chip.
            // Mobile: CSS uses `inset-x-0 bottom-0` so inline style is ignored.
            const btn = event?.currentTarget;
            if (btn && window.innerWidth >= 640) {
                const rect = btn.getBoundingClientRect();
                const top = rect.bottom + window.scrollY + 4;
                const right = Math.max(8, window.innerWidth - rect.right);
                this.qsPopoverStyle = `top: ${top}px; right: ${right}px;`;
            } else {
                this.qsPopoverStyle = '';
            }
            this.qsChipOpen = chipName;
        },

        toggleCollapsed() {
            this.qsCollapsed = !this.qsCollapsed;
            setStoredValue('qs_collapsed', this.qsCollapsed ? '1' : '');
        },

        get minPercent() {
            return ((this.min - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
        },

        get maxPercent() {
            return ((this.max - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
        },

        get filterCount() {
            let count = 0;
            if (this.min !== this.rangeMin || this.max !== this.rangeMax) count++;
            if (this.male) count++;
            if (this.female) count++;
            if (this.selectedCountries.length > 0) count++;
            if (this.selectedLanguages.length > 0) count++;
            if (this.online) count++;
            return count;
        },

        updateMin(val) {
            this.min = Math.min(parseInt(val), this.max - 1);
        },

        updateMax(val) {
            this.max = Math.max(parseInt(val), this.min + 1);
        },

        formatAge(val) {
            return val >= this.rangeMax ? val + '+' : val;
        },

        clearFilters() {
            this.min = this.rangeMin;
            this.max = this.rangeMax;
            this.male = false;
            this.female = false;
            this.selectedCountries = [];
            this.selectedLanguages = [];
            this.online = false;
            window.dispatchEvent(new CustomEvent('clear-quick-search-country'));
            window.dispatchEvent(new CustomEvent('clear-quick-search-language'));
            // Persist immediately so a reload inside the 500ms triggerChange debounce
            // doesn't silently revert the clear from the server-hydrated initial state.
            this.saveFilters();
            this.triggerChange();
        },

        saveFilters() {
            setStoredValue(this.storageKey, JSON.stringify({
                v: 2,
                min: this.min,
                max: this.max,
                male: this.male,
                female: this.female,
                selectedCountries: this.selectedCountries,
                selectedLanguages: this.selectedLanguages,
                online: this.online
            }));
        },

        loadFilters() {
            try {
                const saved = getStoredValue(this.storageKey);
                if (saved) {
                    const data = JSON.parse(saved);
                    if (data.min >= this.rangeMin && data.min <= this.rangeMax) this.min = data.min;
                    if (data.max >= this.rangeMin && data.max <= this.rangeMax) this.max = data.max;
                    if (typeof data.male === 'boolean') this.male = data.male;
                    if (typeof data.female === 'boolean') this.female = data.female;
                    if (typeof data.online === 'boolean') this.online = data.online;

                    // Schema v2 introduced with the server-seeded chip hydration. Pre-v2
                    // saves wrote `selectedCountries: []` / `selectedLanguages: []` on
                    // every Discover load (the chips initialized empty), so an unversioned
                    // empty array is "no device intent yet" and must NOT shadow the new
                    // server seed. From v2 onwards an empty array is meaningful: the user
                    // explicitly cleared on this device, and that beats the server. v2
                    // saves land on every interaction, so the legacy window is self-
                    // healing on first new interaction.
                    const isV2 = data.v === 2;

                    if (Array.isArray(data.selectedCountries)
                        && (isV2 || data.selectedCountries.length > 0)) {
                        this.selectedCountries = data.selectedCountries;
                    } else if (data.selectedCountry && data.selectedCountry !== '---') {
                        // Legacy single-country format
                        this.selectedCountries = [{
                            code: data.selectedCountry,
                            name: data.selectedCountryName || data.selectedCountry
                        }];
                    }

                    if (Array.isArray(data.selectedLanguages)
                        && (isV2 || data.selectedLanguages.length > 0)) {
                        this.selectedLanguages = data.selectedLanguages;
                    }
                }
            } catch (e) {
                // localStorage may be unavailable or data corrupted
            }
        },

        triggerChange() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.saveFilters();
                this.$refs.triggerInput?.dispatchEvent(new Event('change', { bubbles: true }));
            }, 500);
        },

        /**
         * Sets up touch handling for the slider container.
         * Called via x-init on the slider wrapper element.
         */
        initSliderTouch(containerEl) {
            const rangeInputs = containerEl.querySelectorAll('input[type="range"]');
            if (rangeInputs.length < 2) return;

            const minInput = rangeInputs[0];
            const maxInput = rangeInputs[1];
            let activeInput = null;

            const getValueFromTouch = (touch) => {
                const rect = containerEl.getBoundingClientRect();
                const x = touch.clientX - rect.left;
                const percent = Math.max(0, Math.min(1, x / rect.width));
                return Math.round(this.rangeMin + percent * (this.rangeMax - this.rangeMin));
            };

            const handleTouchStart = (e) => {
                e.preventDefault();
                const touch = e.touches[0];
                const value = getValueFromTouch(touch);

                // Determine which thumb is closer
                const distToMin = Math.abs(value - this.min);
                const distToMax = Math.abs(value - this.max);

                if (distToMin <= distToMax) {
                    activeInput = minInput;
                    const newVal = Math.min(value, this.max - 1);
                    minInput.value = newVal;
                    this.updateMin(newVal);
                } else {
                    activeInput = maxInput;
                    const newVal = Math.max(value, this.min + 1);
                    maxInput.value = newVal;
                    this.updateMax(newVal);
                }
            };

            const handleTouchMove = (e) => {
                if (!activeInput) return;
                e.preventDefault();
                const touch = e.touches[0];
                const value = getValueFromTouch(touch);

                if (activeInput === minInput) {
                    const newVal = Math.min(value, this.max - 1);
                    minInput.value = newVal;
                    this.updateMin(newVal);
                } else {
                    const newVal = Math.max(value, this.min + 1);
                    maxInput.value = newVal;
                    this.updateMax(newVal);
                }
            };

            const handleTouchEnd = () => {
                activeInput = null;
            };

            containerEl.addEventListener('touchstart', handleTouchStart, { passive: false });
            containerEl.addEventListener('touchmove', handleTouchMove, { passive: false });
            containerEl.addEventListener('touchend', handleTouchEnd);
        },

        /**
         * Opens the filter modal and locks body scroll
         */
        openModal() {
            this.filtersOpen = true;
            lockBodyScroll();

            this.$nextTick(() => {
                if (window.LucideIcons) {
                    window.LucideIcons.initialize();
                }
            });
        },

        /**
         * Closes the filter modal and unlocks body scroll
         */
        closeModal() {
            this.filtersOpen = false;
            unlockBodyScroll();
        },

        init() {
            this.loadFilters();

            this.$nextTick(() => this.triggerChange());

            this.$watch('min', () => this.triggerChange());
            this.$watch('max', () => this.triggerChange());
            this.$watch('male', () => this.triggerChange());
            this.$watch('female', () => this.triggerChange());
            this.$watch('online', () => this.triggerChange());

            // Close chip popover on document click (chip buttons use @click.stop, popover panel uses @click.stop)
            document.addEventListener('click', () => {
                if (this.qsChipOpen) this.qsChipOpen = null;
            });
        }
    };
}
